/**
 * Disparadores del oficial (`/activa/oficial/disparadores`, 29/10/2026, bloque 2 del plan de campañas): reglas que
 * corren solas a las 09:00 de la marca y arman un BORRADOR de campaña con la gente que las cumple ese día. Nunca mandan
 * nada: el borrador se revisa y se confirma en el asistente, como cualquier campaña.
 *
 * - Dos tipos, sobre la base de ventas en uso: inactividad (hace más de N días que no compra) y después de una compra
 *   (a los N días de su última compra). Arriba se dice hasta qué día trae ventas la base: vieja, no arma nada.
 * - Cada uno: nombre, tipo, días, a quiénes mira (todo el Público o un público de tu base), plantilla y variables, cada
 *   cuánto corre y la ventana en la que no le vuelve a escribir a la misma persona. Nace apagado.
 * - La lista dice si está prendido, cuándo corre y cómo terminó la última corrida (cuántos entraron y el link al
 *   borrador), y con qué base de ventas corrió (su atraso). «Probar ahora» arma el borrador de hoy, prendido o apagado,
 *   para ver a quiénes alcanzaría; la tarjeta dice antes de tocarlo que hasta confirmarlo o cancelarlo no arma otro.
 * - Una PÁGINA con la lista y, abajo de ella, el que se crea o se edita (`?disparador=nuevo` o `?disparador=<id>`, así el
 *   «atrás» del navegador vuelve a donde estaba), como las listas de exclusión.
 * - Del equipo, como el asistente: la ruta va con `PaginaSoloEquipo` y el backend se la niega a un cliente. En el
 *   celular los botones miden 44 px.
 */

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Pause, Pencil, Play, Plus, RefreshCw, Save, Trash2, Zap } from "lucide-react";
import type { CampoDisponible, PlantillaDetalle, PlantillaResumen } from "../types";
import type { PublicoResumen } from "../types.base";
import type { Disparador, ListaDisparadores } from "../types.disparadores";
import { oficialApi } from "../api";
import { baseApi } from "../baseApi";
import { disparadoresApi } from "../disparadoresApi";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { variablesSinClics } from "../logic/clics";
import { imagenPorCampana } from "../logic/imagenes";
import { mapeoInicial } from "../logic/mapeo";
import {
  DIAS_SEMANA,
  FORM_NUEVO,
  LARGO_NOMBRE_DISPARADOR,
  TEXTO_PROBAR_AHORA,
  VENTANA_MAXIMA,
  avisoBase,
  cuandoDe,
  entradaDe,
  erroresDelForm,
  filasFuera,
  formDe,
  motivoParaNoGuardar,
  quienesDe,
  rutaDelBorrador,
  textoBaseDeLaCorrida,
  textoVentana,
  tituloCorrida,
  type FormDisparador,
  type Tono,
} from "../logic/disparadores";
import { MapeoVariablesForm } from "../components/MapeoVariablesForm";
import { fechaHora, num, plural } from "../../campana-nueva/utils/format";

const B = "/activa/oficial";
/** Los botones de la pantalla: 44 px de alto en el celular. */
const BOTON = "inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-semibold disabled:opacity-50";
const TONO: Record<Tono, string> = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-900",
  info: "border-slate-200 bg-slate-50 text-slate-700",
  alerta: "border-amber-200 bg-amber-50 text-amber-900",
  error: "border-red-200 bg-red-50 text-red-800",
};

export default function DisparadoresPage() {
  const [sp, setSp] = useSearchParams();
  const elegido = sp.get("disparador");
  const [lista, setLista] = useState<ListaDisparadores | null>(null);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const cargar = useCallback(async () => {
    setError("");
    try {
      const l = leerRespuesta<ListaDisparadores>(await disparadoresApi.listar());
      if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió los disparadores." : l.error || textoError(l)); return; }
      setLista({ disparadores: Array.isArray(l.data.disparadores) ? l.data.disparadores : [], base: l.data.base ?? null });
    } catch (e) { setError(textoError(leerError(e))); }
  }, []);
  useEffect(() => { void cargar(); }, [cargar]);

  const ir = (d: string | null) => setSp((prev) => {
    const n = new URLSearchParams(prev);
    if (d) n.set("disparador", d);
    else n.delete("disparador");
    return n;
  });
  const reemplazar = (d: Disparador) => setLista((prev) => (prev ? { ...prev, disparadores: prev.disparadores.map((x) => (x.id === d.id ? d : x)) } : prev));
  const editando = elegido && elegido !== "nuevo" ? lista?.disparadores.find((d) => d.id === elegido) : undefined;
  const base = lista ? avisoBase(lista.base, lista.disparadores.some((d) => d.tipo === "inactividad")) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to={`${B}/campanas`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft size={13} /> Campañas
          </Link>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight mt-1">Disparadores</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Reglas que corren solas a las 09:00 y arman un borrador de campaña con la gente que las cumple ese día. Nunca mandan
            nada: el borrador lo revisás y lo confirmás en el asistente, como cualquier campaña.
          </p>
        </div>
        <button type="button" onClick={() => { setAviso(""); ir("nuevo"); }} className={`${BOTON} bg-violet-600 px-4 text-white hover:bg-violet-700`}>
          <Plus size={15} /> Nuevo disparador
        </button>
      </div>

      {base && (
        <p className={`flex items-start gap-2 rounded-xl border px-4 py-2.5 text-sm ${base.alerta ? TONO.alerta : TONO.info}`} role={base.alerta ? "alert" : "status"} data-testid="aviso-base">
          {base.alerta && <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
          <span>
            {base.texto}
            {base.alerta && <> <Link to="/activa/publico" className="font-semibold underline">Ir a Público</Link></>}
          </span>
        </p>
      )}

      {aviso && (
        <p className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" role="status">
          <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> {aviso}
        </p>
      )}

      {elegido && (elegido === "nuevo" || editando) && (
        <EditorDisparador
          key={elegido}
          disparador={editando}
          onCerrar={() => ir(null)}
          onGuardado={(d, nuevo) => {
            setAviso(nuevo ? `Se creó «${d.nombre}». Está apagado: prendelo cuando quieras que corra, o tocá «Probar ahora» para ver a quiénes alcanzaría.` : `Se guardó «${d.nombre}».`);
            void cargar();
            ir(null);
          }}
        />
      )}

      <section aria-label="Los disparadores de la marca" className="space-y-3">
        {error ? (
          <div className="rounded-2xl border border-red-100 bg-white p-5 text-sm text-red-700" role="alert">
            <p>No pudimos traer los disparadores. {error}</p>
            <button type="button" onClick={() => void cargar()} className={`${BOTON} mt-2 px-0 hover:underline`}>
              <RefreshCw size={14} /> Reintentar
            </button>
          </div>
        ) : lista === null ? (
          <div className="space-y-2" aria-busy="true">{[0, 1].map((i) => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
        ) : lista.disparadores.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
            <Zap size={22} className="mx-auto mb-2 text-slate-300" />
            Todavía no hay disparadores. Armá el primero con «Nuevo disparador».
          </div>
        ) : (
          lista.disparadores.map((d) => (
            <TarjetaDisparador
              key={d.id}
              d={d}
              onEditar={() => { setAviso(""); ir(d.id); }}
              onCambio={reemplazar}
              onBorrado={(nombre) => {
                setAviso(`Se borró «${nombre}». Los borradores que había armado siguen en Campañas.`);
                setLista((prev) => (prev ? { ...prev, disparadores: prev.disparadores.filter((x) => x.id !== d.id) } : prev));
                if (elegido === d.id) ir(null);
              }}
            />
          ))
        )}
      </section>
    </div>
  );
}

/** Un disparador de la lista: su regla, su estado, la última corrida y lo que se puede hacer. */
function TarjetaDisparador({ d, onEditar, onCambio, onBorrado }: {
  d: Disparador;
  onEditar: () => void;
  onCambio: (d: Disparador) => void;
  onBorrado: (nombre: string) => void;
}) {
  const [ocupado, setOcupado] = useState<"" | "prender" | "apagar" | "probar" | "borrar">("");
  const [error, setError] = useState("");
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const idProbar = useId();

  const accion = async (cual: "prender" | "apagar" | "probar") => {
    setOcupado(cual);
    setError("");
    try {
      const l = leerRespuesta<Disparador>(await disparadoresApi[cual](d.id));
      if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió el disparador." : l.error || textoError(l)); return; }
      onCambio(l.data);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setOcupado("");
    }
  };

  const borrar = async () => {
    setOcupado("borrar");
    setError("");
    try {
      const l = leerRespuesta<{ borrado: true }>(await disparadoresApi.borrar(d.id));
      if (!l.ok) { setError(l.error || textoError(l)); return; }
      onBorrado(d.nombre);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setOcupado("");
    }
  };

  const plantillaRara = !d.plantilla ? "La plantilla ya no existe: elegí otra." : d.plantilla.archivada ? "La plantilla está archivada: elegí otra." : d.plantilla.estado !== "approved" ? "La plantilla no está aprobada: el borrador va a frenar en el chequeo." : "";
  const u = d.ultimaCorrida;
  const t = u ? tituloCorrida(u) : null;
  const conBase = u ? textoBaseDeLaCorrida(u.base) : null;

  return (
    <article className="space-y-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" aria-label={`El disparador «${d.nombre}»`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">{d.nombre}</h3>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${d.activo ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="estado-disparador">
              {d.activo ? "Prendido" : "Apagado"}
            </span>
          </div>
          <p className="text-sm text-slate-700">{d.quienes}. {d.cuando}.</p>
          <p className="text-xs text-slate-500">
            {d.audiencia.tipo === "base" ? `Del público «${d.audiencia.publicoNombre || "de tu base"}»` : "De todo el Público"} · Plantilla «{d.plantilla?.nombre || "—"}» · {textoVentana(d.ventanaDias)}
          </p>
          <p className="text-xs text-slate-500">
            {d.activo ? (d.proximaCorrida ? `Próxima corrida: ${fechaHora(d.proximaCorrida)}.` : "Prendido.") : "Apagado: no corre solo hasta que lo prendas."}
            {d.corriendo && " Está corriendo ahora."}
          </p>
          {plantillaRara && <p className="flex items-start gap-1.5 text-xs font-semibold text-amber-800"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {plantillaRara}</p>}
        </div>
      </div>

      {u && t && (
        <div className={`space-y-1 rounded-xl border px-3 py-2.5 text-sm ${TONO[t.tono]}`} data-testid="ultima-corrida">
          <p className="font-semibold">
            {t.titulo}
            <span className="ml-2 text-xs font-normal opacity-80">
              {u.origen === "probar" ? "Con «Probar ahora»" : "A su hora"}{u.at ? ` · ${fechaHora(u.at)}` : ""}
            </span>
          </p>
          {u.total !== null && (u.resultado === "armado" || u.resultado === "sin_nadie") && (
            <p className="text-xs">
              De {num(u.total)} {plural(u.total, "persona", "personas")} que mira, {u.entraron ? `${num(u.entraron)} ${plural(u.entraron, "entró", "entraron")}` : "no entró nadie"}.
              {filasFuera(u.fuera).length > 0 && ` Afuera: ${filasFuera(u.fuera).map((f) => `${f.titulo.toLowerCase()} (${num(f.cantidad)})`).join(", ")}.`}
            </p>
          )}
          {u.motivos.map((m, i) => <p key={i} className="text-xs">{m}</p>)}
          {conBase && <p className="text-xs" data-testid="base-de-la-corrida">{conBase}</p>}
          {u.campana && (
            <Link to={rutaDelBorrador(u.campana)} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-violet-700 hover:underline sm:min-h-0">
              {u.campana.estado === "borrador" ? `Abrir el borrador «${u.campana.nombre}»` : `Ver la campaña «${u.campana.nombre}»`}
            </Link>
          )}
        </div>
      )}

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>}

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <button type="button" onClick={() => void accion("probar")} disabled={!!ocupado} aria-describedby={idProbar} className={`${BOTON} border border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100`}>
          {ocupado === "probar" ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} {ocupado === "probar" ? "Armando el borrador…" : "Probar ahora"}
        </button>
        {d.activo ? (
          <button type="button" onClick={() => void accion("apagar")} disabled={!!ocupado} className={`${BOTON} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
            {ocupado === "apagar" ? <Loader2 size={14} className="animate-spin" /> : <Pause size={14} />} Apagar
          </button>
        ) : (
          <button type="button" onClick={() => void accion("prender")} disabled={!!ocupado} className={`${BOTON} border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100`}>
            {ocupado === "prender" ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Prender
          </button>
        )}
        <button type="button" onClick={onEditar} disabled={!!ocupado} className={`${BOTON} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
          <Pencil size={14} /> Editar
        </button>
        {!confirmarBorrar && (
          <button type="button" onClick={() => setConfirmarBorrar(true)} disabled={!!ocupado} className={`${BOTON} text-red-700 hover:bg-red-50`}>
            <Trash2 size={14} /> Borrar
          </button>
        )}
      </div>
      {/* no es un simulacro: el borrador de hoy traba las corridas siguientes hasta que se confirme o se cancele */}
      <p id={idProbar} className="text-xs text-slate-500" data-testid="aviso-probar">{TEXTO_PROBAR_AHORA}</p>

      {confirmarBorrar && (
        <div className="space-y-2 rounded-lg bg-red-50 px-3 py-3 text-sm text-red-800" role="group" aria-label="Confirmar que se borra el disparador">
          <p>¿Borrar «{d.nombre}»? No corre más. Los borradores que ya armó quedan en Campañas.</p>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void borrar()} disabled={!!ocupado} className={`${BOTON} bg-red-600 text-white hover:bg-red-700`}>
              {ocupado === "borrar" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Sí, borrarlo
            </button>
            <button type="button" onClick={() => setConfirmarBorrar(false)} className={`${BOTON} hover:underline`}>No, dejarlo</button>
          </div>
        </div>
      )}
    </article>
  );
}

/** Crear o editar un disparador: la regla, a quiénes mira, la plantilla con sus variables y cada cuánto corre. */
function EditorDisparador({ disparador, onCerrar, onGuardado }: {
  disparador?: Disparador;
  onCerrar: () => void;
  onGuardado: (d: Disparador, nuevo: boolean) => void;
}) {
  const nuevo = !disparador;
  const ids = { nombre: useId(), dias: useId(), ventana: useId(), plantilla: useId(), publico: useId(), dia: useId() };
  const [f, setF] = useState<FormDisparador>(() => (disparador ? formDe(disparador) : FORM_NUEVO));
  const [plantillas, setPlantillas] = useState<PlantillaResumen[] | null>(null);
  const [publicos, setPublicos] = useState<PublicoResumen[] | null>(null);
  const [campos, setCampos] = useState<CampoDisponible[]>([]);
  const [detalle, setDetalle] = useState<PlantillaDetalle | null>(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const cambiar = (x: Partial<FormDisparador>) => setF((prev) => ({ ...prev, ...x }));

  // las plantillas aprobadas de la marca (el backend vuelve a mirar que se puedan usar)
  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const l = leerRespuesta<{ items: PlantillaResumen[] }>(await oficialApi.plantillas({ estado: "approved" }));
        if (vivo) setPlantillas(l.ok && Array.isArray(l.data?.items) ? l.data.items.filter((p) => !p.archivada) : []);
      } catch {
        if (vivo) setPlantillas([]);
      }
    })();
    return () => { vivo = false; };
  }, []);

  // los públicos de la Base, sólo si se elige uno
  useEffect(() => {
    if (f.audienciaTipo !== "base" || publicos !== null) return;
    let vivo = true;
    void (async () => {
      try {
        const l = leerRespuesta<PublicoResumen[]>(await baseApi.publicos());
        if (vivo) setPublicos(l.ok && Array.isArray(l.data) ? l.data : []);
      } catch {
        if (vivo) setPublicos([]);
      }
    })();
    return () => { vivo = false; };
  }, [f.audienciaTipo, publicos]);

  // los datos que tiene la audiencia elegida (una de la Base suma apellido y sus campos)
  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const l = leerRespuesta<CampoDisponible[]>(await oficialApi.campos(f.audienciaTipo));
        if (vivo) setCampos(l.ok && Array.isArray(l.data) ? l.data : []);
      } catch {
        if (vivo) setCampos([]);
      }
    })();
    return () => { vivo = false; };
  }, [f.audienciaTipo]);

  // la plantilla elegida, con sus variables; el mapeo arranca de lo que había (las que siguen existiendo)
  useEffect(() => {
    setDetalle(null);
    if (!f.plantillaId) return;
    let vivo = true;
    void (async () => {
      try {
        const l = leerRespuesta<PlantillaDetalle>(await oficialApi.plantilla(f.plantillaId));
        if (!vivo) return;
        if (!l.ok || !l.data) { setError(l.ok ? "No pudimos traer la plantilla." : l.error || textoError(l)); return; }
        const p = l.data;
        setDetalle(p);
        setF((prev) => ({ ...prev, mapeo: mapeoInicial(variablesSinClics(p.variables, p.botones), prev.mapeo) }));
      } catch (e) {
        if (vivo) setError(textoError(leerError(e)));
      }
    })();
    return () => { vivo = false; };
  }, [f.plantillaId]);

  const variables = useMemo(() => variablesSinClics(detalle?.variables, detalle?.botones), [detalle]);
  const camposValidos = useMemo(() => (campos.length ? campos.map((c) => c.campo as string) : undefined), [campos]);
  const errores = erroresDelForm(f, variables.map((v) => v.numero), camposValidos);
  const sinDetalle = !!f.plantillaId && !detalle;
  const motivo = sinDetalle ? "Esperá a que cargue la plantilla" : motivoParaNoGuardar(errores);
  const erroresVariables = Object.fromEntries(Object.entries(errores).filter(([k]) => k.startsWith("var")).map(([k, v]) => [k.slice(3), v]));
  const dias = Number(f.dias) || 0;
  const maxima = VENTANA_MAXIMA[f.cada];

  const guardar = async () => {
    if (motivo) return;
    setGuardando(true);
    setError("");
    try {
      const cuerpo = entradaDe(f);
      const l = leerRespuesta<Disparador>(await (disparador ? disparadoresApi.editar(disparador.id, cuerpo) : disparadoresApi.crear(cuerpo)));
      if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió el disparador." : l.error || textoError(l)); return; }
      onGuardado(l.data, nuevo);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };

  const radio = (activo: boolean) => `flex min-h-[44px] items-start gap-2 rounded-xl border px-3 py-2.5 text-left text-sm ${activo ? "border-violet-400 bg-violet-50/60" : "border-slate-200 hover:border-slate-300"}`;
  const campo = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500";

  return (
    <section className="space-y-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" aria-label={nuevo ? "Nuevo disparador" : `Editar «${disparador?.nombre || ""}»`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">{nuevo ? "Nuevo disparador" : `Editar «${disparador?.nombre}»`}</h3>
        <button type="button" onClick={onCerrar} className={`${BOTON} text-xs text-slate-500 hover:text-slate-800`}>Cerrar</button>
      </div>
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>}

      <div>
        <label htmlFor={ids.nombre} className="mb-1 block text-xs font-semibold text-slate-600">Nombre</label>
        <input id={ids.nombre} value={f.nombre} maxLength={LARGO_NOMBRE_DISPARADOR + 20} onChange={(e) => cambiar({ nombre: e.target.value })} placeholder="Por ejemplo, «Te extrañamos»" className={`${campo} max-w-sm`} />
        <p className="mt-1 text-xs text-slate-500">El borrador de cada día se llama así, con la fecha: «{(f.nombre.trim() || "Te extrañamos")} · 02/11».</p>
      </div>

      <fieldset className="space-y-2">
        <legend className="mb-1 text-xs font-semibold text-slate-600">Cuándo entra alguien</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className={radio(f.tipo === "inactividad")}>
            <input type="radio" name="tipo-disparador" className="mt-1 accent-violet-600" checked={f.tipo === "inactividad"} onChange={() => cambiar({ tipo: "inactividad" })} />
            <span><span className="block font-semibold text-slate-900">Inactividad</span><span className="text-xs text-slate-500">Hace N días que no compra.</span></span>
          </label>
          <label className={radio(f.tipo === "despues_de_compra")}>
            <input type="radio" name="tipo-disparador" className="mt-1 accent-violet-600" checked={f.tipo === "despues_de_compra"} onChange={() => cambiar({ tipo: "despues_de_compra" })} />
            <span><span className="block font-semibold text-slate-900">Después de una compra</span><span className="text-xs text-slate-500">A los N días de su última compra.</span></span>
          </label>
        </div>
        <div>
          <label htmlFor={ids.dias} className="mb-1 block text-xs font-semibold text-slate-600">{f.tipo === "inactividad" ? "Días sin comprar" : "Días después de la compra"}</label>
          <input id={ids.dias} inputMode="numeric" value={f.dias} onChange={(e) => cambiar({ dias: e.target.value })} className={`${campo} max-w-[8rem]`} />
          {errores.dias && <p className="mt-1 text-xs text-red-600">{errores.dias}</p>}
        </div>
        <p className="text-xs text-slate-500">Mira la base de ventas en uso. Quien no está en ella no entra: no se sabe si compró.</p>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="mb-1 text-xs font-semibold text-slate-600">A quiénes mira</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className={radio(f.audienciaTipo === "publico")}>
            <input type="radio" name="audiencia-disparador" className="mt-1 accent-violet-600" checked={f.audienciaTipo === "publico"} onChange={() => cambiar({ audienciaTipo: "publico" })} />
            <span><span className="block font-semibold text-slate-900">Todo el Público</span><span className="text-xs text-slate-500">Quien dio el sí, de cualquier fuente.</span></span>
          </label>
          <label className={radio(f.audienciaTipo === "base")}>
            <input type="radio" name="audiencia-disparador" className="mt-1 accent-violet-600" checked={f.audienciaTipo === "base"} onChange={() => cambiar({ audienciaTipo: "base" })} />
            <span><span className="block font-semibold text-slate-900">Un público de tu base</span><span className="text-xs text-slate-500">Una lista que armaste en la Base.</span></span>
          </label>
        </div>
        {f.audienciaTipo === "base" && (
          <div>
            <label htmlFor={ids.publico} className="mb-1 block text-xs font-semibold text-slate-600">El público</label>
            <select id={ids.publico} value={f.publicoId} onChange={(e) => cambiar({ publicoId: e.target.value })} className={`${campo} max-w-sm bg-white`} disabled={publicos === null}>
              <option value="">{publicos === null ? "Cargando…" : publicos.length ? "Elegí uno" : "No hay públicos en tu base"}</option>
              {(publicos || []).map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
            {errores.audiencia && <p className="mt-1 text-xs text-red-600">{errores.audiencia}</p>}
          </div>
        )}
        <p className="text-xs text-slate-500">La fatiga de la marca, el consentimiento, las bajas y las listas de exclusión valen como en cualquier campaña.</p>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="mb-1 text-xs font-semibold text-slate-600">Qué les decís</legend>
        <div>
          <label htmlFor={ids.plantilla} className="mb-1 block text-xs font-semibold text-slate-600">Plantilla</label>
          <select id={ids.plantilla} value={f.plantillaId} onChange={(e) => cambiar({ plantillaId: e.target.value })} className={`${campo} max-w-sm bg-white`} disabled={plantillas === null}>
            <option value="">{plantillas === null ? "Cargando…" : plantillas.length ? "Elegí una aprobada" : "No hay plantillas aprobadas"}</option>
            {disparador?.plantilla && !(plantillas || []).some((p) => p.id === disparador.plantilla?.id) && (
              <option value={disparador.plantilla.id}>{disparador.plantilla.nombre}</option>
            )}
            {(plantillas || []).map((p) => <option key={p.id} value={p.id}>{p.nombreVisible}</option>)}
          </select>
        </div>
        {f.plantillaId && (detalle ? (
          <>
            <MapeoVariablesForm
              variables={variables}
              campos={campos}
              mapeo={f.mapeo}
              sinResolver={[]}
              onChange={(m) => cambiar({ mapeo: m })}
              errores={erroresVariables}
              imagen={imagenPorCampana(detalle.mediaUrl)}
            />
            {variables.length > 0 && (
              <p className="text-xs text-slate-500">Con un dato del cliente, elegí ya qué pasa si a alguien le falta: el disparador corre cada vez con otra gente.</p>
            )}
          </>
        ) : (
          <div className="h-24 rounded-xl bg-slate-100 animate-pulse" aria-busy="true" />
        ))}
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="mb-1 text-xs font-semibold text-slate-600">Cada cuánto corre</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className={radio(f.cada === "dia")}>
            <input type="radio" name="cada-disparador" className="mt-1 accent-violet-600" checked={f.cada === "dia"} onChange={() => cambiar({ cada: "dia" })} />
            <span className="font-semibold text-slate-900">Todos los días</span>
          </label>
          <label className={radio(f.cada === "semana")}>
            <input type="radio" name="cada-disparador" className="mt-1 accent-violet-600" checked={f.cada === "semana"} onChange={() => cambiar({ cada: "semana" })} />
            <span className="font-semibold text-slate-900">Una vez por semana</span>
          </label>
        </div>
        {f.cada === "semana" && (
          <div>
            <label htmlFor={ids.dia} className="mb-1 block text-xs font-semibold text-slate-600">Qué día</label>
            <select id={ids.dia} value={f.diaSemana} onChange={(e) => cambiar({ diaSemana: Number(e.target.value) })} className={`${campo} max-w-[12rem] bg-white`}>
              {DIAS_SEMANA.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
        )}
        <div>
          <label htmlFor={ids.ventana} className="mb-1 block text-xs font-semibold text-slate-600">Días sin volver a escribirle a la misma persona</label>
          <input id={ids.ventana} inputMode="numeric" value={f.ventanaDias} onChange={(e) => cambiar({ ventanaDias: e.target.value })} className={`${campo} max-w-[8rem]`} />
          <p className="mt-1 text-xs text-slate-500">
            Quien recibió una campaña de este disparador en esos días no entra al borrador. Hasta {num(maxima)} días {f.cada === "dia" ? "con «todos los días»" : "una vez por semana"}.
          </p>
          {errores.ventanaDias && <p className="mt-1 text-xs text-red-600">{errores.ventanaDias}</p>}
        </div>
      </fieldset>

      <p className="rounded-xl bg-violet-50 px-3 py-2.5 text-sm text-violet-900" data-testid="resumen-regla">
        {dias > 0 ? quienesDe(f.tipo, dias, f.cada) : "A quienes cumplan la regla"}. Corre {cuandoDe(f.cada, f.diaSemana).replace(/^./, (x) => x.toLowerCase())} y arma un borrador: no manda nada solo.
      </p>

      <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
        <button type="button" onClick={() => void guardar()} disabled={!!motivo || guardando} title={motivo || undefined}
          className={`${BOTON} bg-violet-600 px-4 text-white hover:bg-violet-700`}>
          {guardando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {nuevo ? "Crear el disparador" : "Guardar los cambios"}
        </button>
        {motivo && <span className="text-xs text-slate-500">{motivo}</span>}
        {nuevo && <span className="text-xs text-slate-500">Nace apagado.</span>}
      </div>
    </section>
  );
}
